import { useEffect, useId, type JSX, type ReactNode } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { Button, DialogClose } from '@/components';
import type { RefrigerationUnitFormValues } from './refrigerationUnitFormShared';
import { RefrigerationUnitFormError } from './refrigerationUnitFormShared';
import {
  EntityDialog,
  EntityDialogFooter,
  FormErrorBanner,
  FormErrorSummary,
} from '@/components/blocks/forms';
import { getErrorCount, scrollToFirstError } from '@/components/blocks/forms/formErrors';

interface RefrigerationUnitSafetySectionModalProps {
  open: boolean;
  title: string;
  onOpenChange: (open: boolean) => void;
  defaultValues: Partial<RefrigerationUnitFormValues>;
  onSubmit: (values: RefrigerationUnitFormValues) => Promise<void> | void;
  isSubmitting?: boolean;
  children: ReactNode;
}

export function RefrigerationUnitSafetySectionModal({
  open,
  title,
  onOpenChange,
  defaultValues,
  onSubmit,
  isSubmitting = false,
  children,
}: RefrigerationUnitSafetySectionModalProps): JSX.Element {
  const formId = useId();
  const form = useForm<RefrigerationUnitFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, defaultValues, form]);

  const handleSubmit: SubmitHandler<RefrigerationUnitFormValues> = async (values) => {
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof RefrigerationUnitFormError && error.field) {
        form.setError(error.field, {
          type: 'manual',
          message: error.message,
        });
        return;
      }
      form.setError('root', {
        type: 'manual',
        message: 'Impossibile salvare. Riprova o contatta l’assistenza.',
      });
    }
  };

  const errorCount = getErrorCount(form.formState.errors);
  const rootError =
    (form.formState.errors as { root?: { message?: string } }).root?.message ?? null;

  return (
    <EntityDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="md"
      isDirty={form.formState.isDirty}
      isSaving={isSubmitting}
      formId={formId}
      banner={rootError ? <FormErrorBanner message={rootError} /> : null}
      summary={
        errorCount > 0 ? (
          <FormErrorSummary
            count={errorCount}
            onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
          />
        ) : null
      }
      footer={
        <EntityDialogFooter
          secondary={
            <DialogClose asChild>
              <Button variant="ghost" disabled={isSubmitting}>
                Chiudi
              </Button>
            </DialogClose>
          }
          primary={
            <Button type="submit" form={formId} disabled={isSubmitting}>
              {isSubmitting ? 'Salvataggio…' : 'Salva'}
            </Button>
          }
        />
      }
    >
      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleSubmit, (errors) => scrollToFirstError(errors))}
          noValidate
          className="space-y-6"
        >
          <fieldset disabled={isSubmitting}>{children}</fieldset>
        </form>
      </FormProvider>
    </EntityDialog>
  );
}
